import { ageLabel } from '../../../utils/records';
import useAutoRefresh from "../../../hooks/useAutoRefresh";
import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  Typography,
  Card,
  Button,
  Space,
  Select,
  Input,
  Modal,
  message,
  Drawer,
  Descriptions,
  Badge,
  Row,
  Col,
  Statistic,
  Empty,
  Spin,
  List,
  Avatar,
  Divider,
} from "antd";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  PhoneOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  MedicineBoxOutlined,
} from "@ant-design/icons";
import { appointmentAPI } from "../../../services/api";
import { useParams } from "react-router-dom";
import "./DoctorAppointmentsView.css";

const { Title, Text } = Typography;
const { Option } = Select;

const DoctorAppointmentsView = () => {
  const { doctorId } = useParams();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPayment, setFilterPayment] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [viewMode, setViewMode] = useState("list"); // 'list' or 'grid'

  useAutoRefresh(() => fetchAppointments());

  useEffect(() => {
    fetchAppointments();
  }, [doctorId]);

  const fetchAppointments = async () => {
    if (!doctorId) return;
    try {
      setLoading(true);
      const res = await appointmentAPI.getByDoctor(doctorId);
      if (res?.data?.appointments) {
        setAppointments(res.data.appointments);
      } else if (Array.isArray(res?.data)) {
        setAppointments(res.data);
      }
    } catch (error) {
      message.error("Failed to fetch appointments");
      console.error("Error fetching appointments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      await appointmentAPI.update(appointmentId, { status: newStatus });
      message.success(`Appointment ${newStatus} successfully`);
      fetchAppointments();
      if (selectedAppointment?._id === appointmentId) {
        setDrawerVisible(false);
      }
    } catch (error) {
      message.error("Failed to update appointment status");
      console.error("Error updating status:", error);
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    Modal.confirm({
      title: "Cancel Appointment",
      content: "Are you sure you want to cancel this appointment?",
      okText: "Yes, Cancel",
      okType: "danger",
      cancelText: "No",
      centered: true,
      onOk: async () => {
        try {
          await appointmentAPI.cancel(appointmentId);
          message.success("Appointment cancelled successfully");
          fetchAppointments();
          if (selectedAppointment?._id === appointmentId) {
            setDrawerVisible(false);
          }
        } catch (error) {
          message.error("Failed to cancel appointment");
          console.error("Error cancelling appointment:", error);
        }
      },
    });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      pending: "orange",
      confirmed: "blue",
      completed: "green",
      cancelled: "red",
    };
    return statusColors[status?.toLowerCase()] || "default";
  };

  const getPaymentStatusColor = (status) => {
    const paymentColors = {
      paid: "green",
      pending: "orange",
      failed: "red",
    };
    return paymentColors[status?.toLowerCase()] || "default";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    });
  };

  const getFilteredAppointments = () => {
    let filtered = [...appointments];

    if (filterStatus !== "all") {
      filtered = filtered.filter(
        (apt) => apt.status?.toLowerCase() === filterStatus
      );
    }

    if (filterPayment !== "all") {
      filtered = filtered.filter(
        (apt) => apt.paymentStatus?.toLowerCase() === filterPayment
      );
    }

    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(
        (apt) =>
          apt.patient?.name?.toLowerCase().includes(search) ||
          apt.patient?.phone?.includes(search) ||
          apt.doctor?.name?.toLowerCase().includes(search)
      );
    }

    return filtered;
  };

  const getStatistics = () => {
    const total = appointments.length;
    const pending = appointments.filter((a) => a.status === "pending").length;
    const confirmed = appointments.filter(
      (a) => a.status === "confirmed"
    ).length;
    const completed = appointments.filter(
      (a) => a.status === "completed"
    ).length;
    const cancelled = appointments.filter(
      (a) => a.status === "cancelled"
    ).length;

    return { total, pending, confirmed, completed, cancelled };
  };

  const stats = getStatistics();
  const filteredData = getFilteredAppointments();

  const AppointmentCard = ({ appointment }) => (
    <Card
      className="appointment-list-card"
      hoverable
      onClick={() => {
        setSelectedAppointment(appointment);
        setDrawerVisible(true);
      }}
    >
      <div className="appointment-card-header">
        <div className="patient-info">
          <Avatar
            size={48}
            icon={<UserOutlined />}
            style={{ backgroundColor: "#1890ff" }}
          />
          <div className="patient-details">
            <Text strong style={{ fontSize: 16 }}>
              {appointment.patient?.name || "N/A"}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <PhoneOutlined style={{ marginRight: 4 }} />
              {appointment.patient?.phone || "N/A"}
              <span> · Age: {ageLabel(appointment.patient)}</span>
            </Text>
          </div>
        </div>
        <div className="status-badges">
          <Badge
            status={
              appointment.status === "completed"
                ? "success"
                : appointment.status === "cancelled"
                ? "error"
                : "processing"
            }
            text={appointment.status?.toUpperCase()}
          />
        </div>
      </div>

      <Divider style={{ margin: "12px 0" }} />

      <Row gutter={[8, 8]}>
        <Col span={12}>
          <div className="info-item">
            <CalendarOutlined style={{ color: "#0A6EBD", marginRight: 4 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatDate(appointment.appointmentDate)}
            </Text>
          </div>
        </Col>
        <Col span={12}>
          <div className="info-item">
            <ClockCircleOutlined style={{ color: "#1890ff", marginRight: 4 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {appointment.appointmentTime || "N/A"}
            </Text>
          </div>
        </Col>
        <Col span={12}>
          <div className="info-item">
            <MedicineBoxOutlined style={{ color: "#0A6EBD", marginRight: 4 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {appointment.doctor?.name || "N/A"}
            </Text>
          </div>
        </Col>
        <Col span={12}>
          <div className="info-item">
            <DollarOutlined style={{ color: "#faad14", marginRight: 4 }} />
            <Text strong style={{ fontSize: 12, color: "#1890ff" }}>
              ₹{appointment.consultationFee ?? appointment.doctor?.fees ?? 0}
            </Text>
          </div>
        </Col>
      </Row>

      <Divider style={{ margin: "12px 0" }} />

      <div className="card-footer">
        <Badge
          color={getPaymentStatusColor(appointment.paymentStatus)}
          text={
            <Text style={{ fontSize: 11 }}>
              Payment: {appointment.paymentStatus?.toUpperCase()}
            </Text>
          }
        />
      </div>
    </Card>
  );

  return (
    <div className="doctor-appointments-container">
      {/* Header */}
      <div className="appointments-header">
        <div>
          <Title level={2} style={{ margin: 0 }}>
            <CalendarOutlined style={{ marginRight: 12 }} />
            Appointments
          </Title>
          <Text type="secondary">Manage your patient appointments</Text>
        </div>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchAppointments}
          loading={loading}
          size="large"
        >
          Refresh
        </Button>
      </div>

      {/* Statistics */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={6}>
          <Card className="stat-card stat-total">
            <Statistic
              title="Total"
              value={stats.total}
              styles={{ content: { color: "#1890ff", fontSize: 24 } }}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="stat-card stat-pending">
            <Statistic
              title="Pending"
              value={stats.pending}
              styles={{ content: { color: "#0A6EBD", fontSize: 24 } }}
              prefix={<SyncOutlined spin={stats.pending > 0} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="stat-card stat-confirmed">
            <Statistic
              title="Confirmed"
              value={stats.confirmed}
              styles={{ content: { color: "#1890ff", fontSize: 24 } }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="stat-card stat-completed">
            <Statistic
              title="Completed"
              value={stats.completed}
              styles={{ content: { color: "#0A6EBD", fontSize: 24 } }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="filters-card" style={{ marginBottom: 16 }}>
        <Space orientation="vertical" style={{ width: "100%" }} size="middle">
          <Input
            placeholder="Search by patient name or phone..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            size="large"
          />
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12}>
              <Select
                placeholder="Filter by Status"
                style={{ width: "100%" }}
                value={filterStatus}
                onChange={setFilterStatus}
                suffixIcon={<FilterOutlined />}
                size="large"
              >
                <Option value="all">All Status</Option>
                <Option value="pending">Pending</Option>
                <Option value="confirmed">Confirmed</Option>
                <Option value="completed">Completed</Option>
                <Option value="cancelled">Cancelled</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12}>
              <Select
                placeholder="Filter by Payment"
                style={{ width: "100%" }}
                value={filterPayment}
                onChange={setFilterPayment}
                suffixIcon={<FilterOutlined />}
                size="large"
              >
                <Option value="all">All Payments</Option>
                <Option value="paid">Paid</Option>
                <Option value="pending">Pending</Option>
                <Option value="failed">Failed</Option>
              </Select>
            </Col>
          </Row>
        </Space>
      </Card>

      {/* Appointments List */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Loading appointments...</Text>
          </div>
        </div>
      ) : filteredData.length > 0 ? (
        <div className="appointments-list">
          {filteredData.map((appointment) => (
            <AppointmentCard key={appointment._id} appointment={appointment} />
          ))}
        </div>
      ) : (
        <Card>
          <Empty
            description={
              <span>
                No appointments found
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {searchText ||
                  filterStatus !== "all" ||
                  filterPayment !== "all"
                    ? "Try adjusting your filters"
                    : "Appointments will appear here"}
                </Text>
              </span>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      )}

      {/* Appointment Details Drawer */}
      <Drawer
        title={
          <Space>
            <CalendarOutlined />
            <span>Appointment Details</span>
          </Space>
        }
        placement="right"
        width={window.innerWidth < 768 ? "100%" : 480}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        className="appointment-drawer"
      >
        {selectedAppointment && (
          <div>
            {/* Status Badge */}
            <div className="drawer-status-header">
              <Badge
                status={
                  selectedAppointment.status === "completed"
                    ? "success"
                    : selectedAppointment.status === "cancelled"
                    ? "error"
                    : "processing"
                }
                text={
                  <Text strong style={{ fontSize: 18 }}>
                    {selectedAppointment.status?.toUpperCase()}
                  </Text>
                }
              />
            </div>

            <Descriptions
              column={1}
              bordered
              size="middle"
              style={{ marginTop: 20 }}
            >
              <Descriptions.Item
                label={
                  <span>
                    <UserOutlined style={{ marginRight: 8 }} />
                    Patient Name
                  </span>
                }
              >
                <Text strong>{selectedAppointment.patient?.name || "N/A"}</Text>
                <div>Age: {ageLabel(selectedAppointment.patient)}</div>
              </Descriptions.Item>

              <Descriptions.Item
                label={
                  <span>
                    <PhoneOutlined style={{ marginRight: 8 }} />
                    Phone Number
                  </span>
                }
              >
                <Text copyable>
                  {selectedAppointment.patient?.phone || "N/A"}
                </Text>
              </Descriptions.Item>

              <Descriptions.Item
                label={
                  <span>
                    <CalendarOutlined style={{ marginRight: 8 }} />
                    Date
                  </span>
                }
              >
                <Text strong>
                  {formatDate(selectedAppointment.appointmentDate)}
                </Text>
              </Descriptions.Item>

              <Descriptions.Item
                label={
                  <span>
                    <ClockCircleOutlined style={{ marginRight: 8 }} />
                    Time
                  </span>
                }
              >
                <Text strong>
                  {selectedAppointment.appointmentTime || "N/A"}
                </Text>
              </Descriptions.Item>

              <Descriptions.Item label="Booked on">
                {selectedAppointment.createdAt
                  ? dayjs(selectedAppointment.createdAt).format("DD MMM YYYY, hh:mm A")
                  : "N/A"}
              </Descriptions.Item>

              <Descriptions.Item
                label={
                  <span>
                    <MedicineBoxOutlined style={{ marginRight: 8 }} />
                    Doctor
                  </span>
                }
              >
                {selectedAppointment.doctor?.name || "N/A"}
              </Descriptions.Item>

              <Descriptions.Item
                label={
                  <span>
                    <DollarOutlined style={{ marginRight: 8 }} />
                    Fee
                  </span>
                }
              >
                <Text strong style={{ color: "#1890ff", fontSize: 18 }}>
                  ₹{selectedAppointment.consultationFee ?? selectedAppointment.doctor?.fees ?? 0}
                </Text>
              </Descriptions.Item>

              <Descriptions.Item label="Payment">
                <Badge
                  color={getPaymentStatusColor(
                    selectedAppointment.paymentStatus
                  )}
                  text={
                    <Text strong>
                      {selectedAppointment.paymentStatus?.toUpperCase() ||
                        "N/A"}
                    </Text>
                  }
                />
              </Descriptions.Item>

              <Descriptions.Item label="Booking ID">
                <Text
                  copyable
                  style={{
                    fontSize: 11,
                    fontFamily: "monospace",
                    display: "block",
                  }}
                >
                  {selectedAppointment._id}
                </Text>
              </Descriptions.Item>
            </Descriptions>

            {/* Action Buttons */}
            {selectedAppointment.status !== "cancelled" &&
              selectedAppointment.status !== "completed" && (
                <div style={{ marginTop: 24 }}>
                  <Space
                    orientation="vertical"
                    style={{ width: "100%" }}
                    size="middle"
                  >
                    <Divider>Quick Actions</Divider>

                    {selectedAppointment.status === "pending" && (
                      <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        block
                        size="large"
                        onClick={() =>
                          handleStatusChange(
                            selectedAppointment._id,
                            "confirmed"
                          )
                        }
                      >
                        Confirm Appointment
                      </Button>
                    )}

                    {selectedAppointment.status === "confirmed" && (
                      <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        block
                        size="large"
                        style={{
                          background: "#0A6EBD",
                          borderColor: "#0A6EBD",
                        }}
                        onClick={() =>
                          handleStatusChange(
                            selectedAppointment._id,
                            "completed"
                          )
                        }
                      >
                        Mark as Completed
                      </Button>
                    )}

                    <Button
                      danger
                      icon={<CloseCircleOutlined />}
                      block
                      size="large"
                      onClick={() =>
                        handleCancelAppointment(selectedAppointment._id)
                      }
                    >
                      Cancel Appointment
                    </Button>
                  </Space>
                </div>
              )}

            {selectedAppointment.status === "cancelled" && (
              <div className="drawer-footer-info">
                <Badge
                  status="error"
                  text="This appointment has been cancelled"
                />
              </div>
            )}

            {selectedAppointment.status === "completed" && (
              <div className="drawer-footer-info">
                <Badge
                  status="success"
                  text="This appointment has been completed"
                />
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default DoctorAppointmentsView;
